/**
 * Slim auth/bootstrap API for the login cold path.
 * Shares the same BaseApiService instance as the full `@/services/api` facade
 * so session tokens and callbacks stay in sync.
 */
import {
  BaseApiService,
  type LoginCredentials,
  type LoginMfaRequiredResponse,
  type LoginResponse,
  type ResetPasswordRequest,
  isLoginMfaRequired,
} from './base.service';
import type { User } from '@/types/api';

/** Shared transport — also used by `createApiService()` in `./index`. */
export const sharedBaseApi = new BaseApiService();

export const authApi = {
  login: (credentials: LoginCredentials) => sharedBaseApi.login(credentials),
  verifyMfaLogin: (payload: { mfaToken: string; code: string; method?: 'email' | 'totp' | 'recovery' }) =>
    sharedBaseApi.verifyMfaLogin(payload),
  resendMfaCode: (mfaToken: string) => sharedBaseApi.resendMfaCode(mfaToken),
  logout: () => sharedBaseApi.logout(),
  getResetPassword: (code: string) => sharedBaseApi.getResetPassword(code),
  resetPassword: (values: ResetPasswordRequest) => sharedBaseApi.resetPassword(values),
  setUserInfo: (data: LoginResponse) => sharedBaseApi.setUserInfo(data),
  clearUserInfo: () => sharedBaseApi.clearUserInfo(),
  setSessionPermissions: (permissions: string[]) => sharedBaseApi.setSessionPermissions(permissions),
  setAuthFailureCallback: (callback: () => void) => sharedBaseApi.setAuthFailureCallback(callback),
  getCurrentUser: (): Promise<User> => sharedBaseApi.request<User>('users/current'),
};

export type { LoginCredentials, LoginMfaRequiredResponse, LoginResponse, ResetPasswordRequest };
export { isLoginMfaRequired };
export default authApi;
